/* global CustomEvent */

import { defineComponent } from "vue";
import { formatPrice } from "../../js/lib/util";
import store from "../../js/lib/store";
import { isGwpGiftVariantId, clampGwpQuantity, GWP_MAX_LINE_QUANTITY, isGwpCustomerExcluded } from "../../js/lib/gwp-config";
import VueImage from "../image/image.vue";
import QuantitySelector from "../quantity-selector/quantity-selector.vue";

const CartItem = defineComponent({
  name: "cart-item",
  components: {
    image: VueImage,
    "quantity-selector": QuantitySelector,
  },
  template: `
    <div class="cart-item relative" :class="{ 'has-upsell': itemShowUpsell, 'has-upsell-short': itemShowUpsellShort, isBundle: type == 'bundle-order', 'free-product-gwp': isFreeProductGwp }">
      <div class="cart-item__info relative align-t">
        <a :href="item.url" class="cart-item__featured inline-block ratio">
          <div is="vue:image" :src="image" :size="960" :alt="title" spinner-size="sm" css="fit-cover"></div>
        </a>
        <div class="cart-item__details">
          <h4 class="h4 bold cart-item__details__heading" v-html="title"></h4>
          <span v-if="hasSellingPlanAllocation" class="selling-plan-details" :data-sub-item="itemKey">
              <i class="fa-solid fa-repeat"></i> <strong v-text="displaySellingPlanName"></strong>
          </span>

          <div class="cart-item__price-mini-cart lbl" v-if="canChangeQuantity">
            <p>
              <span v-if="isDiscountedPrice" class="cart-item__price-compare" v-html="totalCompareFormattedPrice"></span>
              <span v-html="totalFormattedPrice"></span>
            </p>
          </div>

          <p class="p1 cart-item__details__description" v-if="canChangeQuantity">
            <span v-if="isGiftCard">Amount</span>
            <span v-else v-html="variantTitle"></span>
          </p>

          <div class="p1 mt1 cart-item__details-quantity">
              <div class="f aic" v-if="canChangeQuantity">
                  <div
                  is="vue:quantity-selector"
                  :key="variantId + '-' + quantity"
                  :initial="quantity"
                  :max="quantitySelectorMax"
                  @change="updateQuantity">
                  </div>

                  <button v-if="type == 'bundle-order'" class="cart-item__remove link" @click.prevent="editBundle">
                    Edit bundle
                  </button>

                  <button
                  class="cart-item__remove link ml075"
                  :aria-label="'Remove ' + title"
                  @click.prevent="remove">
                  Remove
                  </button>
              </div>
          </div>
        </div>
      </div>

      <div class="cart-item__price hide align-t align-l" v-if="canChangeQuantity">
        <p v-html="formattedPrice"></p>
      </div>

      <div class="cart-item__quantity align-t align-l" v-if="modLocation !== 'mini-cart'">
          <div class="f aic" v-if="canChangeQuantity">
              <div
              is="vue:quantity-selector"
              :key="variantId + '-' + quantity"
              :initial="quantity"
              :max="quantitySelectorMax"
              @change="updateQuantity">
              </div>

              <button v-if="type == 'bundle-order'" class="cart-item__remove link ml075" @click.prevent="editBundle">
                Edit bundle
              </button>

              <button
              class="cart-item__remove link ml075"
              :aria-label="'Remove ' + title"
              @click.prevent="remove">
              Remove
              </button>
          </div>
      </div>
      <div class="hide cart-item__total" v-if="canChangeQuantity">
        <p class="cart-item__total-inner align-r" v-html="totalFormattedPrice"></p>
      </div>
      <div class="cart-item__upsell" v-if="showItemUpsell" v-html="itemUpsell"></div>
    </div>
  `,
  data: function () {
    return {
      itemShowUpsell: false,
      itemShowUpsellShort: false,
      itemSellingPlans: [],
      itemHasCheckedUpsell: false,
      itemConvertingToSellingPlan: false,
      baseUrl: (window as any).locale_aware_base_url?.replace("/", "") || "",
      store,
    };
  },
  props: {
    item: Object,
    image: String,
    title: String,
    price: Number,
    quantity: Number,
    index: Number,
    type: String,
    compare_at_price: Number,
    tags: String,
    variantId: Number,
    variantTitle: String,
    itemKey: String,
    modLocation: {
      type: String,
      default: "default",
    },
  },
  methods: {
    cartLineId(): string {
      return this.itemKey || this.variantId?.toString() || "";
    },
    updateQuantity(quantity: number): void {
      const capped = isGwpGiftVariantId(this.variantId)
        ? clampGwpQuantity(quantity)
        : quantity;

      void store.updateItemQuantity({
        quantity: capped,
        id: this.cartLineId(),
        variantId: this.variantId,
      });

      if (this.modLocation !== "mini-cart") {
        document.dispatchEvent(
          new CustomEvent("product:added", {
            detail: {
              variant: this.item,
              quantity: quantity,
              isCart: true,
            },
          })
        );
      }
    },
    editBundle() {
      // hide the page so they don't see the cart change
      document.body.style.display = "none";

      // remove the current bundle from the cart
      store.updateItemQuantity({
        quantity: 0,
        id: this.cartLineId(),
        variantId: this.variantId,
      });

      // take the user back to the bundle page
      window.location.href =
        "/collections/build-your-own-anti-anxious-station?preloadBundle=" +
          this.item?.sku || "";
    },
    remove() {
      void store.updateItemQuantity({
        quantity: 0,
        id: this.cartLineId(),
        variantId: this.variantId,
      });
    },
    canChangeQuantity() {
      return this.price && this.price > 0;
    },
  },
  computed: {
    showItemUpsell(): boolean {
      return !isGwpCustomerExcluded();
    },
    hasSellingPlanAllocation(): boolean {
      const allocation = (this.item as Record<string, any>)
        ?.selling_plan_allocation;
      const planId = allocation?.selling_plan?.id;

      return planId != null && planId !== "" && Number(planId) !== 0;
    },
    displaySellingPlanName(): string {
      if (!this.hasSellingPlanAllocation) {
        return "";
      }

      return (
        (this.item as Record<string, any>).selling_plan_allocation.selling_plan
          .name || ""
      );
    },
    quantitySelectorMax() {
      if (isGwpGiftVariantId(this.variantId)) {
        return GWP_MAX_LINE_QUANTITY;
      }
      return 99;
    },
    formattedPrice() {
      return `${formatPrice(this.price)}`;
    },
    isGiftCard() {
      return this.type?.toLowerCase() === "gift card";
    },

    lineFinalPrice(): number {
      const item = this.item as any;
      if (item && typeof item.final_line_price === "number") {
        return item.final_line_price;
      }
      return this.price && this.quantity ? this.price * this.quantity : 0;
    },
    lineOriginalPrice(): number {
      const item = this.item as any;
      const qty = this.quantity || 0;

      // "was" price from a product sale (compare-at)
      const compareTotal = this.compare_at_price
        ? this.compare_at_price * qty
        : 0;

      // "was" price before line-level discounts (discount codes / automatic
      // discounts) as reported by Shopify's cart line item
      const originalLinePrice =
        item && typeof item.original_line_price === "number"
          ? item.original_line_price
          : 0;

      return Math.max(compareTotal, originalLinePrice);
    },
    isDiscountedPrice(): boolean {
      return (
        this.lineFinalPrice > 0 &&
        this.lineOriginalPrice > this.lineFinalPrice
      );
    },
    isFreeProductGwp() {
      const raw = (this.item as any)?.properties?.["_free-product"];
      if (raw === undefined || raw === null) {
        return false;
      }
      return raw === 1 || raw === "1" || raw === true;
    },
    totalComparePrice() {
      return `${formatPrice(this.compare_at_price || 0)}`;
    },
    totalCompareFormattedPrice() {
      return `${formatPrice(this.lineOriginalPrice)}`;
    },
    totalFormattedPrice() {
      return `${formatPrice(this.lineFinalPrice)}`;
    },
    itemUpsell() {
      if (isGwpCustomerExcluded()) {
        return "";
      }

      // if this product is a custom bundle then we need to see if they have
      // the max products to get a discount
      if (this.type === "bundle-order") {
        if (this.item?.sku) {
          const parts = this.item?.sku.split("|");
          let total = 0;
          parts.forEach((part: string) => {
            const lines = part.split("@");
            total += parseInt(lines[0]);
          });

          if (total < 4) {
            this.itemShowUpsellShort = true;
            const short = 4 - total;
            return `Add ${short} more item for 15% off`;
          }
        }

        return;
      }

      if (this.hasSellingPlanAllocation) {
        // we already have this on subscription. Ensure that we have
        // an automatic discount applied by making a GET fetch
        try {
          // show loading spinner
          const cartFooter = document.querySelector(".cart__footer");

          if (cartFooter) {
            (cartFooter as HTMLElement).style.pointerEvents = "none";
            (cartFooter as HTMLElement).style.opacity = "0.2";
          }

          const { host, protocol } = window.location;
          fetch(`${protocol}//${host}/discount/FREESUBSHIPPING`)
            .then((response) => {
              // enable the page, hide the spinner
            })
            .catch((e) => {
              //
            })
            .finally(() => {
              if (cartFooter) {
                (cartFooter as HTMLElement).style.pointerEvents = "auto";
                (cartFooter as HTMLElement).style.opacity = "1";
              }
            });
        } catch (e) {
          //
        }

        return ``;
      }

      const upsellEnabled = true;

      if (!this.itemHasCheckedUpsell) {
        fetch(
          window.Shopify.routes.root +
            "products/" +
            this.item.handle +
            ".js?locale=" +
            window.Shopify.locale
        )
          .then((response) => {
            return response.json();
          })
          .then((product) => {
            if (
              product.selling_plan_groups &&
              product.selling_plan_groups.length > 0
            ) {
              this.itemShowUpsell = upsellEnabled ? true : false;
              this.itemSellingPlans =
                product.selling_plan_groups[0].selling_plans;
            }
          });

        this.itemHasCheckedUpsell = true;
      }

      if (this.itemConvertingToSellingPlan) {
        return `<div class="cart-item__upsell-inner"><div class="spinner"></div></div>`;
      }

      if (!upsellEnabled) {
        // remove cart-item__upsell from the DOM
        const upsSellContainer = document.querySelector(
          ".cart-item__upsell"
        ) as HTMLElement;

        if (upsSellContainer) {
          upsSellContainer.remove();
        }
      }

      if (upsellEnabled && this.itemShowUpsell) {
        const options = this.itemSellingPlans.map((plan) => {
          return `<option value="${plan.id}">${plan.name}</option>`;
        });

        const select = document.createElement("select");
        // push a default option onto options array
        options.unshift(`<option>Select an option</option>`);

        select.innerHTML = options.join("");

        const div = document.createElement("div");
        div.innerHTML = `<div class="cart-item__upsell-inner"><label class="group-label">
                    <input type="checkbox" name="convert_to_selling_plan" /><div class="group-header">Subscribe and save</div>
                    </label><div class="plans-container"><i class="fa-solid fa-chevron-down"></i></div></div>`;

        div.querySelector(".plans-container")?.appendChild(select);

        // add body event listening for the select
        select.addEventListener("change", (e) => {
          this.itemConvertingToSellingPlan = true;

          // disable the add button
          var cartFooter = document.querySelector(".cart__footer");

          if (cartFooter) {
            (cartFooter as HTMLElement).style.pointerEvents = "none";
            (cartFooter as HTMLElement).style.opacity = "0.1";
          }

          // remove the current key from the cart
          fetch(
            window.Shopify.routes.root +
              "cart/change.js?quantity=0&id=" +
              this.item.id
          )
            .then((response) => {
              fetch(
                window.Shopify.routes.root +
                  "cart/add.js?quantity=1&id=" +
                  this.item.id +
                  "&selling_plan=" +
                  e.target.value
              )
                .then((response) => {
                  return response.json();
                })
                .then((data) => {
                  // add #cart to the URL
                  window.location.hash = "cart";

                  window.location.reload();
                })
                .catch(() => {
                  window.location.reload();
                });
            })
            .catch((e) => {
              window.location.reload();
            });
        });

        return div.innerHTML;
      } else {
        return ``;
      }
    },
  },
});

export default CartItem;
